import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { deneignementService } from '@/services/deneignementService';
import { zoneService } from '@/services/zoneService';
import { equipeService } from '@/services/equipeService';
import { getBackendUrl } from '@/services/api';
import { useAuthStore, useIsGidRegul } from '@/store/authStore';
import { useAuthorization } from '@/hooks/useAuthorization';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ZoneProgressCard } from '@/components/zones';
import { ZONE_LABELS, TYPE_LABELS, PRIORITE_LABELS } from '@/types';
import type { DeneignementZone } from '@/types';
import axios from 'axios';
import {
  ArrowLeftIcon,
  PencilIcon,
  DocumentArrowDownIcon,
  ClockIcon,
  MapIcon,
  ExclamationCircleIcon,
  NoSymbolIcon,
  ArrowPathIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

/**
 * Page detail d'une intervention de deneigement
 */
export function DeneignementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isGidRegul = useIsGidRegul();
  const { canAccess } = useAuthorization();
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  // Correction d'horodatage de jalon (RG-12, SFD jalonnement Contact/Large -
  // Lot 5 #126), reservee a la regulation/admin.
  const [correctingZone, setCorrectingZone] = useState<DeneignementZone | null>(null);
  const [correctionHeureDebut, setCorrectionHeureDebut] = useState('');
  const [correctionHeureFin, setCorrectionHeureFin] = useState('');
  const [correctionMotif, setCorrectionMotif] = useState('');
  // Reaffectation apres incident (#127) : equipe choisie par jalon (DeneignementZone.id)
  const [reassignSelections, setReassignSelections] = useState<Record<number, number>>({});

  const handleDownloadPdf = async () => {
    if (!intervention) return;
    setDownloadingPdf(true);
    try {
      const token = useAuthStore.getState().token;
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/deneignements/${intervention.id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Erreur HTTP ' + response.status);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bon_intervention_${intervention.numeroOrdre}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      toast.error('Erreur lors du telechargement du PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const { data: intervention, isLoading, error } = useQuery({
    queryKey: ['deneignement', id],
    queryFn: () => deneignementService.get(Number(id)),
    enabled: !!id,
  });

  const { data: zonesData } = useQuery({
    queryKey: ['deneignement-zones', id],
    queryFn: () => zoneService.listDeneignementZones(Number(id)),
    enabled: !!id && !!intervention,
  });

  // Reaffectation d'un jalon en incident a une autre equipe (SFD jalonnement
  // Contact/Large, Lot 5 - #127), reservee a la regulation/admin.
  const { data: equipes } = useQuery({
    queryKey: ['equipes-actives'],
    queryFn: () => equipeService.list(),
    enabled: isGidRegul,
  });

  // Filtrer les zones terminees
  const zonesEffectuees = zonesData?.zones.filter(
    (z: DeneignementZone) => z.statut === 'termine'
  ) || [];

  const correctionMutation = useMutation({
    mutationFn: (data: { motif: string; heureDebut?: string; heureFin?: string }) =>
      zoneService.corrigerHorodatageJalon(Number(id), correctingZone!.zone.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement-zones', id] });
      toast.success('Horodatage du jalon corrige');
      setCorrectingZone(null);
    },
    onError: (err: unknown) => {
      const message = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
      toast.error(message || 'Erreur lors de la correction de l\'horodatage');
    },
  });

  const openCorrection = (dz: DeneignementZone) => {
    setCorrectingZone(dz);
    setCorrectionHeureDebut(dz.heureDebut ? new Date(dz.heureDebut).toTimeString().slice(0, 5) : '');
    setCorrectionHeureFin(dz.heureFin ? new Date(dz.heureFin).toTimeString().slice(0, 5) : '');
    setCorrectionMotif('');
  };

  const submitCorrection = () => {
    if (!correctingZone || !correctionMotif.trim()) {
      toast.error('Un motif est requis');
      return;
    }

    // Seule l'heure est modifiable (la date reste celle du jalon d'origine,
    // ou celle du jour si jamais demarre).
    const baseDate = correctingZone.heureDebut ? new Date(correctingZone.heureDebut) : new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    // Chaine locale SANS suffixe de fuseau (ni "Z", ni "+02:00") : le backend
    // (`new \DateTime($data['heureDebut'])`, DeneignementZoneController::
    // corrigerHorodatage()) interprete une chaine sans fuseau comme une heure
    // murale dans le fuseau par defaut du serveur - exactement la convention
    // deja utilisee partout ailleurs dans l'app (demarrer()/terminer() posent
    // `new \DateTime()` sans fuseau explicite). Utiliser `.toISOString()`
    // (toujours en UTC) desynchronisait l'heure de 2h (decalage Europe/Paris)
    // par rapport a l'affichage - bug trouve en verifiant visuellement #126.
    const buildLocalDateTimeString = (hhmm: string): string | undefined => {
      if (!hhmm) return undefined;
      const [h, m] = hhmm.split(':').map(Number);
      const d = new Date(baseDate);
      d.setHours(h, m, 0, 0);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    correctionMutation.mutate({
      motif: correctionMotif.trim(),
      heureDebut: buildLocalDateTimeString(correctionHeureDebut),
      heureFin: buildLocalDateTimeString(correctionHeureFin),
    });
  };

  // Jalons remis "a faire" suite a un incident signale (#127) : pas de flag
  // dedie cote donnees (signalerIncident() ne fait qu'historiser et remettre
  // le statut a A_FAIRE), rapprochement textuel sur le libelle du jalon dans
  // l'historique 'jalon_incident' - meme pattern deja utilise au Lot 4 pour
  // l'asterisque de correction dans le bon PDF.
  const jalonsEnIncident = (zonesData?.zones || []).filter((dz: DeneignementZone) => {
    // Une fois une equipe reaffectee, le jalon disparait de cette liste -
    // le travail de la regulation est termine, la suite (redemarrage du
    // jalon) revient a l'agent de la nouvelle equipe.
    if (dz.zone.configuration == null || dz.statut !== 'a_faire' || dz.equipe != null) return false;
    const libelle = dz.zone.libelle ?? dz.zone.nom;
    return (intervention?.historiques || []).some(
      (h) => h.action === 'jalon_incident' && h.description?.includes(libelle)
    );
  });

  const reassignMutation = useMutation({
    mutationFn: ({ zoneId, equipeId }: { zoneId: number; equipeId: number }) =>
      zoneService.assignEquipe(Number(id), zoneId, { equipe_id: equipeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement-zones', id] });
      toast.success('Jalon reaffecte');
    },
    onError: (err: unknown) => {
      const message = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
      toast.error(message || 'Erreur lors de la reaffectation');
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement de l'intervention...</p>
      </div>
    );
  }

  if (error || !intervention) {
    const status = axios.isAxiosError(error) ? error.response?.status : null;

    if (status === 404) {
      return (
        <div className="text-center py-12">
          <NoSymbolIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-700 font-medium mb-1">Intervention introuvable</p>
          <p className="text-gray-500 text-sm mb-4">Cette intervention n'existe pas ou a ete supprimee.</p>
          <Link to="/deneignements" className="btn-secondary inline-flex items-center">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Retour a la liste
          </Link>
        </div>
      );
    }

    if (status === 403) {
      return (
        <div className="text-center py-12">
          <NoSymbolIcon className="w-12 h-12 mx-auto text-red-300 mb-3" />
          <p className="text-gray-700 font-medium mb-1">Acces refuse</p>
          <p className="text-gray-500 text-sm mb-4">Vous n'avez pas les droits pour voir cette intervention.</p>
          <Link to="/deneignements" className="btn-secondary inline-flex items-center">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Retour a la liste
          </Link>
        </div>
      );
    }

    return (
      <div className="text-center py-12">
        <ExclamationCircleIcon className="w-12 h-12 mx-auto text-red-300 mb-3" />
        <p className="text-gray-700 font-medium mb-1">Une erreur est survenue</p>
        <p className="text-gray-500 text-sm mb-4">Impossible de charger l'intervention. Veuillez reessayer.</p>
        <div className="flex items-center justify-center space-x-3">
          <Link to="/deneignements" className="btn-secondary inline-flex items-center">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Retour a la liste
          </Link>
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['deneignement', id] })}
            className="btn-primary inline-flex items-center"
          >
            <ArrowPathIcon className="w-4 h-4 mr-2" />
            Reessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/deneignements"
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {intervention.numeroOrdre}
            </h2>
            <p className="text-gray-500">
              {new Date(intervention.dateIntervention).toLocaleDateString('fr-FR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <StatusBadge statut={intervention.statut} size="lg" />
          {canAccess.gestionDeneignement && intervention.statut === 'en_attente' && (
            <Link
              to={`/deneignements/${intervention.id}/modifier`}
              className="btn-secondary"
            >
              <PencilIcon className="w-5 h-5 mr-2" />
              Modifier
            </Link>
          )}
          {canAccess.gestionDeneignement && (['affectee', 'en_deplacement', 'en_cours'].includes(intervention.statut)) && (
            <Link
              to={`/deneignements/${intervention.id}/zones`}
              className="btn-primary"
            >
              <MapIcon className="w-5 h-5 mr-2" />
              Gerer les zones
            </Link>
          )}
          {intervention.statut === 'validee' && (
            <button
              className="btn-primary"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              {downloadingPdf ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
              ) : (
                <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
              )}
              {downloadingPdf ? 'Telechargement...' : 'Telecharger PDF'}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informations principales */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold">Informations generales</h3>
            </div>
            <div className="card-body">
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm text-gray-500">Compagnie</dt>
                  <dd className="font-medium">
                    {intervention.compagnie.codeIata} - {intervention.compagnie.nom}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Heure souhaitee</dt>
                  <dd className="font-medium">
                    {intervention.heureSouhaitee
                      ? new Date(intervention.heureSouhaitee).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                      : '-'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Zone</dt>
                  <dd className="font-medium">{ZONE_LABELS[intervention.zone]}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Type d'intervention</dt>
                  <dd className="font-medium">{TYPE_LABELS[intervention.typeIntervention]}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Priorite</dt>
                  <dd className={`font-medium ${
                    intervention.priorite === 'critique' ? 'text-red-600' :
                    intervention.priorite === 'urgente' ? 'text-orange-600' : ''
                  }`}>
                    {PRIORITE_LABELS[intervention.priorite]}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Epaisseur neige</dt>
                  <dd className="font-medium">
                    {intervention.epaisseurNeige ? `${intervention.epaisseurNeige} cm` : '-'}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-sm text-gray-500">Parking</dt>
                  <dd className="font-medium">
                    {intervention.parking?.code} - {intervention.parking?.nom || intervention.parking?.aeroport}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Immatriculation avion</dt>
                  <dd className={`font-medium ${!intervention.immatriculationAvion ? 'text-gray-400 italic' : ''}`}>
                    {intervention.immatriculationAvion || 'Sans avion present'}
                  </dd>
                </div>
                {intervention.commentaire && (
                  <div className="col-span-2">
                    <dt className="text-sm text-gray-500">Commentaire</dt>
                    <dd className="font-medium">{intervention.commentaire}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Duree d'intervention */}
          {intervention.dureeIntervention && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">Donnees d'intervention</h3>
              </div>
              <div className="card-body">
                <dl>
                  <div>
                    <dt className="text-sm text-gray-500">Duree</dt>
                    <dd className="font-medium">
                      {intervention.dureeIntervention} min
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          )}

          {/* Jalons en incident, en attente de reaffectation (#127) */}
          {isGidRegul && jalonsEnIncident.length > 0 && (
            <div className="card border-orange-200">
              <div className="card-header bg-orange-50">
                <h3 className="text-lg font-semibold text-orange-800 flex items-center gap-2">
                  <ExclamationCircleIcon className="w-5 h-5" />
                  Jalons en incident - reaffectation
                </h3>
              </div>
              <div className="card-body space-y-3">
                {jalonsEnIncident.map((dz: DeneignementZone) => (
                  <div key={dz.id} className="flex flex-wrap items-center gap-3">
                    <span className="min-w-[180px] text-sm font-medium">
                      Jalon {dz.zone.numero} - {dz.zone.libelle ?? dz.zone.nom}
                    </span>
                    <select
                      className="input text-sm flex-1 min-w-[160px]"
                      value={reassignSelections[dz.id] ?? ''}
                      onChange={(e) =>
                        setReassignSelections((prev) => ({ ...prev, [dz.id]: Number(e.target.value) }))
                      }
                    >
                      <option value="">Choisir une equipe...</option>
                      {(equipes || []).map((eq) => (
                        <option key={eq.id} value={eq.id}>{eq.nom}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => {
                        const equipeId = reassignSelections[dz.id];
                        if (!equipeId) {
                          toast.error('Choisissez une equipe');
                          return;
                        }
                        reassignMutation.mutate({ zoneId: dz.zone.id, equipeId });
                      }}
                      disabled={reassignMutation.isPending}
                      className="px-3 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium disabled:opacity-50"
                    >
                      Reaffecter
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Zones effectuees */}
          {zonesEffectuees.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">Zones effectuees</h3>
              </div>
              <div className="card-body p-0">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Zone</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Equipe</th>
                      <th className="px-4 py-2 text-center font-medium text-gray-500">Debut</th>
                      <th className="px-4 py-2 text-center font-medium text-gray-500">Fin</th>
                      <th className="px-4 py-2 text-center font-medium text-gray-500">Duree</th>
                      <th className="px-4 py-2 text-center font-medium text-gray-500"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {zonesEffectuees.map((dz: DeneignementZone) => {
                      const estJalon = dz.zone.configuration != null;
                      return (
                        <tr key={dz.id}>
                          <td className="px-4 py-2 font-medium">
                            {estJalon
                              ? `${dz.zone.numero} - ${dz.zone.libelle ?? dz.zone.nom}`
                              : `Z${dz.zone.numero} - ${dz.zone.nom}`}
                          </td>
                          <td className="px-4 py-2 text-gray-600">
                            {dz.equipe?.nom || '-'}
                          </td>
                          <td className="px-4 py-2 text-center">
                            {dz.heureDebut
                              ? new Date(dz.heureDebut).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                              : '-'}
                          </td>
                          <td className="px-4 py-2 text-center">
                            {dz.heureFin
                              ? new Date(dz.heureFin).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                              : '-'}
                          </td>
                          <td className="px-4 py-2 text-center">
                            {dz.dureeMinutes != null ? `${dz.dureeMinutes} min` : '-'}
                          </td>
                          <td className="px-4 py-2 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <CheckCircleIcon className="w-5 h-5 text-green-500 inline" />
                              {estJalon && isGidRegul && (
                                <button
                                  onClick={() => openCorrection(dz)}
                                  className="p-1 rounded hover:bg-gray-100"
                                  title="Corriger l'horodatage"
                                  aria-label="Corriger l'horodatage"
                                >
                                  <PencilIcon className="w-4 h-4 text-gray-400" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Photos */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold">Photos</h3>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">Photo AVANT</p>
                  {intervention.photoAvant ? (
                    <img
                      src={getBackendUrl(intervention.photoAvant)}
                      alt="Photo avant intervention"
                      className="w-full h-80 object-contain rounded-lg bg-gray-50 border"
                    />
                  ) : (
                    <div className="w-full h-80 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                      Pas de photo
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">Photo APRES</p>
                  {intervention.photoApres ? (
                    <img
                      src={getBackendUrl(intervention.photoApres)}
                      alt="Photo apres intervention"
                      className="w-full h-80 object-contain rounded-lg bg-gray-50 border"
                    />
                  ) : (
                    <div className="w-full h-80 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                      Pas de photo
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Progression zones - SFD v2.1 */}
          {(['affectee', 'en_deplacement', 'en_cours', 'cloturee'].includes(intervention.statut)) && (
            <ZoneProgressCard deneignementId={intervention.id} />
          )}

          {/* Equipe */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold">Equipe affectee</h3>
            </div>
            <div className="card-body">
              {intervention.equipes.length > 0 ? (
                intervention.equipes.map((equipe) => (
                  <div key={equipe.id} className="mb-4 last:mb-0">
                    <p className="font-medium">{equipe.nom}</p>
                    <p className="text-sm text-gray-500">{equipe.nombreAgents} agents</p>
                  </div>
                ))
              ) : (
                <p className="text-gray-500">Aucune equipe affectee</p>
              )}
            </div>
          </div>

          {/* Historique */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold">Historique</h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {intervention.historiques?.map((h) => (
                  <div key={h.id} className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <ClockIcon className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        {h.description || (h.nouveauStatut ? `Statut: ${h.nouveauStatut}` : h.action)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(h.horodatage).toLocaleString('fr-FR')} - {h.user?.nomComplet || 'Système'}
                      </p>
                      {h.commentaire && h.commentaire !== h.description && (
                        <p className="text-xs text-gray-600 mt-1">{h.commentaire}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal correction horodatage jalon (RG-12, #126) */}
      {correctingZone && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl p-5 max-w-sm w-full">
            <h3 className="text-lg font-bold mb-1">Corriger l'horodatage</h3>
            <p className="text-sm text-gray-500 mb-4">
              Jalon {correctingZone.zone.numero} - {correctingZone.zone.libelle ?? correctingZone.zone.nom}
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Debut</label>
                <input
                  type="time"
                  value={correctionHeureDebut}
                  onChange={(e) => setCorrectionHeureDebut(e.target.value)}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fin</label>
                <input
                  type="time"
                  value={correctionHeureFin}
                  onChange={(e) => setCorrectionHeureFin(e.target.value)}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motif (requis)</label>
                <input
                  type="text"
                  value={correctionMotif}
                  onChange={(e) => setCorrectionMotif(e.target.value)}
                  placeholder="Ex: erreur de saisie agent"
                  className="input w-full"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setCorrectingZone(null)}
                disabled={correctionMutation.isPending}
                className="flex-1 py-2 px-4 border rounded-lg text-gray-700"
              >
                Annuler
              </button>
              <button
                onClick={submitCorrection}
                disabled={correctionMutation.isPending || !correctionMotif.trim()}
                className="flex-1 py-2 px-4 bg-primary-600 text-white rounded-lg font-medium disabled:opacity-50"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeneignementDetailPage;
